// The shared machinery every reducer case is built out of.
//
// Split out of `commands.ts`, which had reached two thousand lines and thirty
// cases with **nine planned phases queued to add more**. The plan's own §12.6
// names it the contention point and recommended exactly this split; a file
// every feature has to edit is a file every feature conflicts in.
//
// Arranged so that nothing here imports a *value* from `commands.ts`. The
// case modules take their types from there with `import type`, which is
// erased at build time, and their helpers from here — that is the whole of
// why there is no runtime import cycle, and it is worth keeping. A cycle
// between a reducer and its helpers works right up until module evaluation
// order changes, and then fails as an undefined function at startup.

// The command log is the save format, the undo mechanism, the replay
// mechanism, the telemetry substrate and (from Phase 4) the network protocol.
// Every state change goes through `applyCommand` — nothing may mutate
// GameState directly.

import { stampHurt } from "./regen";
import { kinglessGrace } from "./campaign/despair";
import {
  type Color,
  fileOf,
  FORWARD_FACING,
  KING,
  PAWN,
  pieceColor,
  type PieceType,
  pieceType,
  rankOf,
  type Side,
  STRIDE,
} from "./board";
import { isAttackedByEnemyOf } from "./chess/attacks";
import { canBeHurt, legalMoves } from "./chess/moveGen";
import { gameStatus, type GameStatus } from "./chess/status";
import { materialLinks } from "./industry/adjacency";
import {
  acceptsUnit,
  capacityAt,
  consumesAt,
} from "./industry/machines";
import {
  addHeap,
  addToBuffer,
  beltOnAt,
  beltOwnerAt,
  blueprintOwnerAt,
  bufferTotal,
  clearBlueprint,
  cloneIndustry,
  type DepositRow,
  clearDeposit,
  depositAt,
  hasBelt,
  heapAt,
  type IndustryState,
  liveSquares,
  makeUnit,
  markHeap,
  pushUnit,
  dropOnGround,
  droppedAt,
  removeUnit,
  setBelt,
  hasStructure,
  type StructureKind,
  setDeposit,
  setHeap,
  structureAt,
  stripHoldings,
  takeFromGround,
  unitAt
} from "./industry/state";
import {
  type Move,
  moveFrom,
  movePromotion,
  moveTo,
  isBash
} from "./move";
import {
  addKing,
  bindingRoyal,
  setKingKind,
  clonePosition,
  hasCell,
  obstructsAt,
  pieceAt,
  type Position,
  livingSides,
  setCells,
  removePiece,
  setFacing,
  setPiece,
  setPromotionUses,
  resetTurn,
  hurtAt,
  setHurt,
  maxHealthOf,
  platingAt,
  setPlating,
  wardAt
} from "./position";
import {
  RULES,
  SURRENDER_VALUE,
} from "./rules";
import { COAL, depositFault, FUEL, type MaterialLine, mintBill } from "./materials";
import { fodderMovesFor, movesForSide } from "./campaign/titan";

import type { RegionSetup } from "./campaign/level";
import { picketTarget, unicornSquares } from "./unicorn";
import { claimStock, newStructure, standStructure } from "./industry/structures";
import { wreckBelt } from "./industry/beltParts";
import { type TurretShot } from "./tick";
import type { GameState } from "./commands";

export class IllegalCommandError extends Error {}
/**
 * Refuse a turn that would end with your own king attacked.
 *
 * Three commands spend a turn without being a move — `forge`, `build` and
 * `demolish` — and every one of them changes the board: a minted piece lands
 * on a square, a structure takes one off the board, and taking a structure
 * down puts one back. So each can resolve a check *and* each can walk into
 * one, and none of them went through the legality filter `legalMoves` applies
 * to a move.
 *
 * Without this a side in check could forge every turn and sit there for the
 * rest of the game. It is deliberately the ordinary rule of chess — you may
 * not end your turn in check — rather than the blanket refusal `re-aim` uses,
 * because re-aiming moves nothing and these three genuinely can block a line.
 */
export function requireKingSafe(position: Position, owner: Side, what: string): void {
  // The binding royal, so a side holding a spare may forge, build and demolish
  // while one of its kings is under attack — the same unconstrained state the
  // move filter gives it. One reading of "am I in check", not two.
  const king = bindingRoyal(position, owner);
  if (king === undefined || king < 0) return;
  if (isAttackedByEnemyOf(position, king, owner)) {
    throw new IllegalCommandError(`cannot ${what} into check`);
  }
}
export function cloneState(state: GameState): GameState {
  return {
    position: clonePosition(state.position),
    industry: cloneIndustry(state.industry),
    history: state.history,
    repetitions: state.repetitions,
    status: state.status,
    lastTick: state.lastTick,
    lastGambit: state.lastGambit,
    seed: state.seed,
    // Shallow on purpose: every reducer path that changes a run replaces the
    // whole object rather than mutating it, so sharing it with the previous
    // state is safe and copying it per ply would be waste. The one exception
    // is a card effect, which is handed the *cloned* state — so those replace
    // it too.
    run: state.run,
    // Shared for the same reason and under the same rule: `addTally` returns a
    // new object, and nothing anywhere mutates one in place.
    tally: state.tally,
    lastStage: state.lastStage,
    // Shared like `tally`, and safe for the same reason: `addTally` returns a
    // new object and nothing mutates one in place.
    runTally: state.runTally,
    // **Carried, or the round is lost on the next command.** This clone
    // enumerates its fields rather than spreading, which is what makes a
    // forgotten one silent: the state would keep its board and quietly go back
    // to stage one on the following move.
    stage: state.stage,
    // Same rule, same failure: the board would keep its padding, its ore and
    // its markers while the game forgot which map they came from, so "New
    // game" would hand back the chess opening one move in.
    map: state.map,
    // Same rule again: the dials are immutable once a game is open, so the
    // object is shared — and a forgotten line here would quietly hand the game
    // back to the default ruleset on the very next command.
    ruleset: state.ruleset
  };
}
/**
 * Resolve a (from, to, promotion) triple against the legal move list.
 *
 * Going through legal moves rather than trusting the caller is what makes the
 * reducer safe to run on an authoritative server against a hostile client, and
 * it means the UI never has to know about move flags.
 */
export function findLegalMove(
  pos: Position,
  from: number,
  to: number,
  promotion?: PieceType
): Move | undefined {
  const candidates = legalMoves(pos).filter(
    // **A bash is named by its target, never by where the attacker stops.**
    // The player clicks the piece they are hitting, and a bash's `to` is where
    // the *attacker* ends up — one square short of the target whenever the
    // shove was refused, which is a square the same piece can very often also
    // move to quietly. Matching a bash on `to` as well made those two
    // indistinguishable: two candidates, no promotion to choose between them,
    // and the reducer threw `illegal move` at a perfectly ordinary quiet move.
    //
    // So a quiet move or a lethal capture is found on `to`, and a bash only on
    // `shoved`. Unambiguous in both directions — a bash's `shoved` square holds
    // an enemy piece, so nothing else can land there.
    (m) => moveFrom(m) === from && (isBash(m) ? m.shoved === to : moveTo(m) === to)
  );
  if (candidates.length <= 1) return candidates[0];
  // Several candidates means a promotion; default to queen when unspecified.
  return candidates.find((m) => movePromotion(m) === (promotion ?? 5));
}
/**
 * The next army round the cycle that still has pieces on the board.
 *
 * Only used where stages turn over, which is rare — `nextSide` stays a plain
 * modulo so that make/unmake, which runs millions of times in perft, does not
 * scan the board to answer whose turn it is.
 */
/**
 * Pass the clock over an army that cannot move while a teammate still can.
 *
 * **A stuck ally is not the end of the game — it is a stuck ally.** `gameStatus`
 * answers for whoever holds the clock, so before allied armies there was only one
 * reading of "no legal moves": the game is over. With an allied army there are
 * three, and two of them were already handled — your own army stuck is a loss
 * (`defeatReason`), an enemy stuck is a cleared front (`stageTurnsOver`). The
 * third fell through both: an allied army boxed in froze the run with the status
 * line reading Stalemate, no defeat card, and no front turning over. Reported
 * from a real run.
 *
 * So a side with a living **ally** that can still move simply idles, and the
 * clock moves on. Non-destructive on purpose: a stalemate in a three-army game
 * is usually temporary — the piece doing the boxing-in moves, or somebody takes
 * it — so surrendering the army would spend it on a position that unsticks
 * itself a turn later.
 *
 * Inert with two armies. Every side defaults to its own alliance, so the
 * alliance test finds no candidate and stalemate stays stalemate; the same is
 * true of an enemy camp, which is why the stage boundary is untouched. And the
 * turn is only moved once a candidate is *proven* to have a move, so an
 * alliance that is stuck as a whole ends exactly where it used to.
 */
export function idleStuckSides(position: Position, industry: IndustryState): void {
  if (legalMoves(position).length > 0) return;

  const stuck = position.turn;
  const alive = livingSides(position);
  for (let step = 1; step < position.sides; step++) {
    const candidate = ((stuck + step) % position.sides) as Side;
    if (!alive.includes(candidate)) continue;
    if (position.alliances[candidate] !== position.alliances[stuck]) continue;

    position.turn = candidate;
    if (legalMoves(position).length > 0) {
      // A fresh allowance: the side that idled did not spend its moves, and
      // the one taking over has not started yet.
      handClockTo(position, candidate, industry);
      return;
    }
  }
  // Nobody on the team can move. That is a real ending, and it is filed
  // against the side it happened to.
  position.turn = stuck;
}

/**
 * The status, once the clock has been settled onto somebody who can play.
 *
 * One function because the two steps must never be separated: computing a
 * status for a side that is about to be passed over reports an ending that is
 * not happening.
 */
export function settledStatus(next: GameState): GameStatus {
  idleStuckSides(next.position, next.industry);
  return gameStatus(
    next.position,
    legalMoves(next.position).length,
    next.repetitions,
    !next.run,
    kinglessGrace(next.run)
  );
}

/**
 * Hand the clock to a side, the way `passTurn` would have.
 *
 * **A turn is a side *and* a number of moves**, and the four places that hand
 * the clock over directly — a front turning over, a surrender, the two dev
 * tools — were setting the first and leaving the second. `movesLeft` is a
 * property of the *position*, not of a side, so whatever the outgoing turn had
 * left was inherited by whoever arrived: play the Reactor Boosters, mate the
 * camp with the first of the three moves, and the front that lands takes the
 * other two.
 * Reported from a run as an enemy bishop moving twice, which is exactly what
 * it was.
 *
 * One body so the fifth site cannot forget it, which is the same argument
 * `passTurn` itself makes about there being three ways to spend a turn.
 * `idleStuckSides` already did this by hand and now says so by calling this.
 */
export function handClockTo(position: Position, side: Side, industry: IndustryState): void {
  position.turn = side;
  // **The side's own allowance, not the game's**, because Industrial Titan is
  // a thing one side holds and `movesPerTurn` is a property of the whole game.
  // The industry is required, not optional (§26 A2): the grant is Titan Relics
  // standing on the board, and an optional argument here is a refill that
  // silently misses it at whichever call site forgets — on a board with no
  // relic the answer is `movesPerTurn` exactly, as it always was.
  position.movesLeft = movesForSide(position, industry, side);
  position.fodderLeft = fodderMovesFor(position, industry, side);
  // And nothing of the new turn spent yet (§31 T2–T4): a landing or a
  // surrender that hands the clock directly starts a fresh turn too.
  resetTurn(position);
}

export function livingSideAfter(
  position: Position,
  side: Side,
  arriving: ReadonlySet<Side> = new Set()
): Side {
  const alive = new Set<Side>();
  for (const piece of position.board.values()) alive.add(pieceColor(piece));

  // Skip the army that just landed: a camp that materialises in formation and
  // then immediately moves has effectively taken two turns, and the stage
  // before it ended with someone earning a breath. Two passes rather than one
  // so that if the newcomer is all that is left, it still gets the clock.
  for (const skipNew of [true, false]) {
    for (let step = 1; step <= position.sides; step++) {
      const candidate = (side + step) % position.sides;
      if (!alive.has(candidate)) continue;
      if (skipNew && arriving.has(candidate)) continue;
      return candidate;
    }
  }
  return side;
}

/** Scrap left by a piece dying: its chess value plus everything it was holding. */
/**
 * What a piece leaves behind when it dies.
 *
 * One body, two prices. Being captured and surrendering at a stage boundary
 * are the same event industrially — the piece becomes scrap where it stood,
 * its cargo spills with it, and the heap remembers whose it was — and they differ only in what a *king* is worth, because
 * a king is never captured and so has no capture value.
 *
 * Kept as one function on purpose. These were two near-identical copies, and
 * two copies of "what happens when a piece dies" is where the next thing that
 * kills a piece — Sacrifice (7f), health reaching zero (9e), a turret —
 * quietly gets only half the behaviour.
 */
export function wreckPiece(
  industry: IndustryState,
  square: number,
  piece: number,
  /** What the wreck is worth: `PIECE_VALUE` taken, `SURRENDER_VALUE` given up. */
  value: number
): void {
  const held = stripHoldings(industry, square);
  // **A corpse buries the seam** (§26 F3). A body dropped on a tile of a field
  // destroys what was under it — the charge, what it was, and any
  // half-finished dig — and what is left is the ordinary metal scrap a death
  // leaves. **One tile, not the patch**: the rest of the field is untouched,
  // so burying a seam is a local act rather than a way to delete a resource.
  // Never a pump either — the wreck does not inherit the deposit's grade, so a
  // pawn dying on electrum leaves a pawn's worth of iron.
  clearDeposit(industry, square);
  addHeap(industry, square, value + held);
  // The scrap remembers what it was. A pile drawn as an anonymous cone threw
  // away the thing a player most wants from a glance across the board.
  markHeap(industry, square, pieceColor(piece), pieceType(piece));
}

/**
 * A capture beside the capturer's altar: the altar consumes the body whole
 * (§26 A1), instead of `wreckPiece`'s scrap.
 *
 * The victim's **full mint bill**, refined and clean-masked (no refinery made
 * these — the sacrifice case's argument), lands in the altar's stock. That is
 * 2× the piece's value plus the queen's electrum, deliberately better than
 * the heap it replaces: it is the *enemy's* material, so there is no pump to
 * fence — you cannot feed your own pieces to it, and covering more of the
 * front with altars is a strategy priced in squares and stone (§26.6 9).
 *
 * Consumed whole means: no heap, no corpse mark, and no deposit burial, since no corpse
 * ever lay on the tile. Held cargo follows into the stock as grade-0 metal
 * (grade does not survive its carrier; only the floor it lands on moved).
 * Overflow is set down on the square, not scrapped. A Monarch's pack is paid
 * off the board, not the wreck (`payForMonarchs`).
 */
export function claimPieceAtAltar(
  industry: IndustryState,
  square: number,
  piece: number,
  altarSq: number
): void {
  const altar = structureAt(industry, altarSq)!;
  claimPiece(industry, square, piece, (unit) => claimStock(altar, unit));
}

/**
 * The capture bonus into a Kris bearer's inventory (§31 A3): the altar's
 * claim with the bearer's inbox for its stock — "or with the equipped
 * Monarch". Overflow is set down on the victim's square.
 */
export function claimPieceToBearer(
  industry: IndustryState,
  square: number,
  piece: number,
  bearer: number
): void {
  claimPiece(industry, square, piece, (unit) => pushUnit(industry, bearer, unit, "in"));
}

/** The victim's full mint bill, refined, plus its cargo as grade-0 metal, into `keep`. */
function claimPiece(
  industry: IndustryState,
  square: number,
  piece: number,
  keep: (unit: number) => boolean
): void {
  const held = stripHoldings(industry, square);
  const yielded: number[] = [];
  for (const row of mintBill(pieceType(piece))) {
    for (let i = 0; i < row.count; i++) yielded.push(makeUnit(row.grade, 0, row.line));
  }
  for (let i = 0; i < held; i++) yielded.push(makeUnit(0));
  for (const unit of yielded) {
    if (!keep(unit)) dropOnGround(industry, square, unit);
  }
}

/**
 * Put deposits on the board (§26 F3).
 *
 * A **patch is many rows** — a field is a dozen-odd contiguous tiles of one
 * line — so this places tiles and knows nothing about the shape of a field.
 *
 * **Refused, never defaulted** for a rung no ladder has —
 * putting iron down instead of an unknown material would be two clients
 * replaying one log onto two different boards. **Skipped**, silently, for a
 * tile something is already on: a field is generated against the ground a camp
 * opens and the reducer places the camp's pieces first, so a tile under an
 * arriving knight is ordinary rather than exceptional.
 *
 * One writer, shared by the opening command and by `next-stage`, for the
 * reason `openRegion` is shared: the second is written months after the first.
 */
export function placeDeposits(
  position: Position,
  industry: IndustryState,
  rows: readonly DepositRow[]
): void {
  for (const [sq, line, grade, amount, tier] of rows) {
    const fault = depositFault(line, grade, tier ?? 0);
    if (fault) throw new IllegalCommandError(`bad deposit: ${fault}`);
    if (amount <= 0) continue;
    // **Its own test, narrower than `isBare`.** Ore is *in* the ground, so a
    // deposit blocks nothing and `isBare` rightly ignores it — which leaves
    // this the one place that has to refuse laying a second field over the
    // first, and the only three things that genuinely cannot share the tile: a
    // piece standing on it, a building that has paved it, and a corpse, whose
    // scrap would be dug as the seam's material.
    if (!hasCell(position, sq)) continue;
    if (pieceAt(position, sq) !== 0 || hasStructure(industry, sq)) continue;
    if (heapAt(industry, sq) > 0 || depositAt(industry, sq) > 0) continue;
    setDeposit(industry, sq, line as MaterialLine, grade, amount, tier ?? 0);
  }
}

/** Is this side down to its king? */
export function aloneWithKing(position: Position, side: Color): boolean {
  if (position.counts[side] !== 1) return false;
  const king = position.kings[side];
  return king >= 0 && pieceType(pieceAt(position, king)) === KING;
}

/** A piece that was taken. */
export const spillPiece = (
  industry: IndustryState,
  square: number,
  piece: number
  // **`SURRENDER_VALUE`, and the two are now the same call.** The distinction
  // between a captured body and a surrendered one was only ever about the
  // king: every other piece is priced identically in both tables, and a king
  // was worth nothing when captured *because it never was*. A spare king can
  // be, so a corpse worth nought would make killing one leave no scrap on the
  // board — the one piece whose death is most worth marking.
): void => wreckPiece(industry, square, piece, SURRENDER_VALUE[pieceType(piece)]);

/**
 * A piece whose army surrendered at a stage boundary.
 *
 * The king is the difference: worth nothing when captured, because it never
 * is, and it used to be the one piece on a beaten board that left no scrap.
 */
export const surrenderPiece = (
  industry: IndustryState,
  square: number,
  piece: number
): void => wreckPiece(industry, square, piece, SURRENDER_VALUE[pieceType(piece)]);


/**
 * The same, for an army that surrenders at a stage boundary rather than one
 * that is taken a piece at a time. The difference is the king, which is worth
 * nothing when captured — because it never is — and which used to be the one
 * piece on a beaten board that left no scrap behind it.
 */
/**
 * Put a region's ground, pieces and industry on the board.
 *
 * Shared by opening a level and by `reveal-region`, so a region placed at the
 * start and one revealed by an objective cannot drift apart — which they
 * would, because the second is written months after the first.
 */
export function openRegion(state: GameState, region: RegionSetup): void {
  const { position, industry } = state;

  // **Buildings first.** A structure stands on a cell (§31 S1) and
  // `standStructure` lays the floor under one authored off it; pieces below
  // never land on an obstruction.
  for (const [square, kind, owner] of region.structures ?? []) {
    if (hasStructure(industry, square) || pieceAt(position, square) !== 0) continue;
    // Through `standStructure`, so the position's owner mirror is written
    // beside the industry's record — a building recorded in the industry alone
    // is a wall nothing can capture (§26 F5).
    standStructure(position, industry, square, newStructure(kind as StructureKind, owner));
  }

  if (region.cells.length > 0) {
    // A building's square is already a cell (`standStructure`).
    setCells(position, [
      ...position.cells,
      ...region.cells.filter((sq) => !hasStructure(industry, sq))
    ]);
  }

  // **Before the pieces**, because `machineType` is asked the moment one
  // lands: a king re-declared a line later has already been filed.
  for (const [side, kind] of region.kings ?? []) setKingKind(position, side, kind);

  for (const [square, piece] of region.pieces) {
    if (!hasCell(position, square) || pieceAt(position, square) !== 0) continue;
    // Nobody stands on an obstruction (§31 S1).
    if (obstructsAt(position, square)) continue;
    setPiece(position, square, piece);
    if (pieceType(piece) === PAWN) {
      setFacing(position, square, FORWARD_FACING[pieceColor(piece)]);
    }
    if (pieceType(piece) === KING) addKing(position, pieceColor(piece), square);
  }

  // Facing markers override the side's forward, and run *after* the pieces so
  // the order of layers in the map cannot decide the answer.
  for (const [square, direction] of region.facing) {
    if (pieceType(pieceAt(position, square)) === PAWN) setFacing(position, square, direction);
  }
  for (const [square, amount] of region.heaps) {
    if (hasCell(position, square)) setHeap(industry, square, amount);
  }
  // Authored ore (a Tiled `deposit`), through the one writer: refused for a
  // rung no ladder has, skipped under a piece.
  if (region.deposits) placeDeposits(position, industry, region.deposits);
  // Refined material on the floor, as `industry.dropped` — the ground. A king
  // landing on it lifts it through the same sweep that lifts scrap, so this
  // needs nothing taught to `collect` or to the renderer.
  for (const [square, grade, count] of region.metal ?? []) {
    if (!hasCell(position, square)) continue;
    for (let i = 0; i < count; i++) {
      dropOnGround(industry, square, makeUnit(grade as 0 | 1 | 2 | 3));
    }
  }
  // Coal the same way, on the fuel line (§31 M2).
  for (const [square, count] of region.coal ?? []) {
    if (!hasCell(position, square)) continue;
    for (let i = 0; i < count; i++) dropOnGround(industry, square, makeUnit(COAL, 0, FUEL));
  }
  for (const [square, uses] of region.promotion) {
    if (hasCell(position, square)) setPromotionUses(position, square, uses);
  }
  for (const [square, direction, owner] of region.belts) {
    if (hasCell(position, square)) setBelt(industry, square, direction, owner, 1);
  }
}
/**
 * An army lays down its arms: everything it had becomes scrap where it stood,
 * and its king leaves a crown.
 *
 * One implementation, shared by the stage boundary and by an objective that
 * beats a side — `next-stage` and `reveal-region` differ only in what gates
 * them, and the reward for beating an army must not depend on which.
 */
export function surrenderArmy(state: GameState, side: Side): void {
  for (const sq of liveSquares(state.position.board)) {
    const piece = pieceAt(state.position, sq);
    if (pieceColor(piece) !== side) continue;
    surrenderPiece(state.industry, sq, piece);
    removePiece(state.position, sq);
  }

  // **And its lanes go with it.**
  //
  // A belt belongs to an army, not to the ground: `beltOwner` is who may
  // re-point it, who may lift it, and who it refuses to hand material to. An
  // army with no pieces can do none of those, so its network was left on the
  // board as track nobody could use, nobody could remove, and nothing could
  // ever feed — and on a run's board, which only grows, every cleared front
  // added another dead grid across the ground the next one has to be fought on.
  //
  // Wrecked rather than deleted, a plowed part's reading (`wreckBelt`, §31
  // S2): grade is destroyed with its carrier, so the cargo is scrap and the
  // fabric cobble on the floor, exactly as the army's pieces become metal.
  for (const sq of liveSquares(state.industry.beltDir)) {
    if (beltOwnerAt(state.industry, sq) !== side) continue;
    wreckBelt(state.position, state.industry, sq);
  }
  // And its unbuilt runs go too — nothing was ever spent on them, so they
  // simply stop being there. Left behind they would be drawn as somebody's
  // plans on ground the next front is fought over, waiting for iron from an
  // army that no longer exists.
  for (const sq of liveSquares(state.industry.blueprintDir)) {
    if (blueprintOwnerAt(state.industry, sq) === side) clearBlueprint(state.industry, sq);
  }

  // **And the draw clocks reset, because this is the largest capture there is.**
  //
  // A single capture resets `halfmove` and empties the repetition map
  // (`irreversible` in `applyMove`). Sweeping a whole army did neither — so the
  // side that had just won a fight inherited a fifty-move counter that had been
  // running for the entire war, and then drew. Reported from watch mode as
  // exactly that: an army beat another, started building out its factory, and
  // the game was declared a draw while it worked, because building makes no
  // captures and moves no pawns and so never resets anything.
  //
  // The repetition map goes with it for the same reason it goes on a capture:
  // every key in it describes a board with this army still on it, and not one
  // of those positions can occur again. Keeping them is carrying history that
  // can only ever be wrong.
  state.position.halfmove = 0;
  state.repetitions = new Map();
}

/**
 * The queen sweeps every square she crossed, ending on her own, and unloads
 * onto the first belt of hers she can reach — hers is the only piece whose
 * *path* does anything, which is what makes moving her feel like a machine
 * rather than a piece with a buffer.
 *
 * She lifts two different things, and they are stored two different ways:
 *
 * - **Scrap**, which is a *count* of grade-0 material on the floor. She is a
 *   collector, not a refinery, so it stays grade 0.
 * - **Loose units**, which are whole packed words. This is what lifting a lane
 *   leaves behind and where a dropped crown lands, and it used to be the one
 *   thing on the board her path rolled straight over — reported as the queen
 *   not picking things up, which is exactly what it looked like. They are kept
 *   as units rather than counted because grade, refinery provenance and
 *   artifact-ness all live in the word: counting them would melt a crown and
 *   a bar of steel into two of scrap.
 *
 * The haul goes onto the first of her own running parts her path crosses. If
 * she crosses none it rides in her buffer until a later sweep does — except an
 * **artifact**, which never goes on a lane whatever she crosses. A crown's only
 * sink is your king (`sinksFor`) and the carry gradient already answers *hold*
 * for one, so putting it on the first lane that happens to be underfoot is a
 * detour at best. She carries it, which is what a carrier does with one.
 *
 * Nothing she cannot fit is destroyed: anything the belt and her buffer both
 * refuse goes back on the ground where it was. Quantity is conserved across a
 * tick (PLAN §3), and "she swept it up and it vanished" is the same bug report
 * this function exists to answer.
 *
 * Squares are visited from her origin outward, so a long move down a lane
 * drops its haul in a stable, replayable order.
 */
export function vacuumAlong(
  position: Position,
  industry: IndustryState,
  from: number,
  to: number,
  owner: Color
): number {
  const df = Math.sign(fileOf(to) - fileOf(from));
  const dr = Math.sign(rankOf(to) - rankOf(from));
  const step = dr * STRIDE + df;
  if (step === 0) return 0;

  /**
   * Is the move a straight line — a rank, a file or a true diagonal?
   *
   * **The loop below terminates by *arriving*, so a mover that is not walking
   * a ray never stops.** Every piece that sweeps was a queen or a king, and
   * both only ever moved in lines, so the assumption held silently until
   * Imitate let a king borrow a knight's pattern: stepping diagonally from d4
   * toward e6 goes e5, f6, g7 and never lands on e6. It walks off the board
   * forever, inside `applyMove`, so nothing in generation or legality can see
   * it — reported from play as the game locking up on a king that had copied a
   * knight, and only when it moved in the borrowed shape.
   */
  const straight =
    df === 0 ||
    dr === 0 ||
    Math.abs(fileOf(to) - fileOf(from)) === Math.abs(rankOf(to) - rankOf(from));

  let lifted = 0;
  /**
   * Everything the sweep took off the ground, kept separately from `lifted`
   * because `lifted` is spent as the haul is put down.
   *
   * **This is how a king walking over scrap counts as work.** `tally.mined`
   * was only ever `runMining`'s dig pass, so a king — which cannot dig at all
   * — collected material and the run's own scorecard said it had collected
   * nothing. A `collect` objective reads that number.
   */
  let taken = 0;
  let dropPoint = -1;
  // Where each unit came from, so anything that will not fit anywhere can be
  // put back exactly where she found it rather than quietly ceasing to exist.
  const swept: { unit: number; sq: number }[] = [];

  // **A hop has no path, so it lifts only what it lands on.** Starting the
  // sweep at the destination is exactly that: one square, then the break
  // below. Which is also the right reading of the rule — a king "lifts what it
  // lands on", and the squares between are ones it flew over rather than
  // crossed.
  for (let sq = straight ? from : to; ; sq += step) {
    // **A sweep does not mine.** She lifts what is lying on the square, and a
    // deposit is ore *in the ground* — it is dug a tile at a time by something
    // standing on it, which is the whole of what makes a field finite ground
    // to fight over rather than a pile a queen can cross and pocket. Free
    // rather than guarded: with deposits out of the heap map there is nothing
    // here to exclude, and `deposits.test.ts` pins it.
    const scrap = heapAt(industry, sq);
    lifted += scrap;
    taken += scrap;
    setHeap(industry, sq, 0);

    while (droppedAt(industry, sq).length > 0) {
      const unit = takeFromGround(industry, sq);
      taken++;
      swept.push({ unit, sq });
    }

    // The first of her own running parts she rolls over is where the haul
    // goes. Her own cargo is left alone — she is delivering to this lane, not
    // robbing it.
    if (
      dropPoint < 0 &&
      hasBelt(industry, sq) &&
      beltOwnerAt(industry, sq) === owner &&
      beltOnAt(industry, sq) === 1
    ) {
      dropPoint = sq;
    }

    if (sq === to) break;
  }

  // The first lane she crossed takes the haul.
  if (dropPoint >= 0) {
    while (lifted > 0 && pushUnit(industry, dropPoint, makeUnit(0), "cargo")) lifted--;
    for (let i = swept.length - 1; i >= 0; i--) {
      if (!pushUnit(industry, dropPoint, swept[i].unit, "cargo")) break;
      swept.splice(i, 1);
    }
    // A lane with no more room leaves the remainder on the floor beneath it,
    // where it is visible and can be swept up again.
    if (lifted > 0) {
      addHeap(industry, dropPoint, lifted);
      lifted = 0;
    }
  }

  // Whatever the lane would not take travels with her, and anything she has no
  // room for either goes back on the ground it came off.
  for (const { unit, sq } of swept) {
    if (!pushUnit(industry, to, unit, "in")) dropOnGround(industry, sq, unit);
  }
  if (lifted > 0) addToBuffer(industry, to, 0, lifted);

  // Then hand what she is carrying to a machine she has ended up touching —
  // this is the case that made the queen look broken: she would sweep a file,
  // stop right beside a foundry, and hold onto the lot, because unloading
  // only ever looked for belts. A pawn has handed straight to a neighbouring
  // machine since machines started grabbing; she should too, rather than
  // requiring a one-part lane between two touching pieces.
  //
  // Her existing load goes with it, so a queen that has been hoarding empties
  // out when she finally reaches somewhere that wants it.
  //
  // Per *unit*, through `acceptsUnit`, not per square: "is a consumer" is the
  // wrong question to ask about a crown, and now that her path lifts artifacts
  // she can be carrying one. `acceptsUnit` is where "your king and nothing
  // else" is already written down; asking it here rather than repeating the
  // rule is what stops her posting your crown into the nearest foundry.
  if (bufferTotal(industry, to, "in") === 0) return taken;

  for (const adj of materialLinks(position, industry, to)) {
    const piece = pieceAt(position, adj);
    if (!piece || pieceColor(piece) !== owner) continue;
    if (!consumesAt(position, adj)) continue;

    let room = capacityAt(position, industry, adj) - bufferTotal(industry, adj, "in");
    let moved = false;
    for (let i = 0; room > 0; i++) {
      const held = bufferTotal(industry, to, "in");
      if (i >= held) break;
      const unit = unitAt(industry, to, i, "in");
      if (!acceptsUnit(position, industry, adj, owner, unit)) continue;
      removeUnit(industry, to, i, "in");
      pushUnit(industry, adj, unit, "in");
      room--;
      moved = true;
      i--;
    }
    if (moved) return taken;
  }
  return taken;
}
export function requireOwnTurn(state: GameState): Color {
  if (state.status !== "playing" && state.status !== "check") {
    throw new IllegalCommandError(`game is over (${state.status})`);
  }
  return state.position.turn;
}
/**
 * The gate on every owner-bound command: construction, machine policy, and the
 * manual mine and hand-off.
 *
 * **Turn-bound since 2026-09-09 (§29 U1), and that is what it is for.** These
 * commands still cost no tempo — laying track is not a move — but a *turn* has
 * to hold one side's actions and nothing else, or undo and history have no
 * boundary to work against: taking back "your most recent action since your
 * turn began" is meaningless in a log where the opponent's belts are
 * interleaved with your own. The cost accepted is one tick of latency on an
 * action taken in reaction to the opponent's turn; machines act only at the
 * tick that closes each turn, so nothing is actually lost.
 *
 * **`owner` stays on the command** rather than being read off `position.turn`,
 * because the server binds it to the seat (`rooms.ts`) — the two checks catch
 * different lies. This one refuses acting out of turn; the server's refuses
 * acting as somebody else.
 */
export function requireBuilder(state: GameState, owner: Color): Color {
  if (state.status !== "playing" && state.status !== "check") {
    throw new IllegalCommandError(`game is over (${state.status})`);
  }
  if (!Number.isInteger(owner) || owner < 0 || owner >= state.position.sides) {
    throw new IllegalCommandError("not a player");
  }
  if (owner !== state.position.turn) {
    throw new IllegalCommandError("not your turn");
  }
  return owner;
}

/**
 * Take health off a piece, and kill it if that reaches zero.
 *
 * **A piece's health is `PIECE_HEALTH`, not `PIECE_VALUE`** (§27 H2). It was
 * the latter, on the argument that one number should mean what a piece is
 * worth in a trade, what it leaves as scrap, how much it absorbs and what it
 * costs to make whole — and the four came apart the moment a *capture* started
 * asking: toughness tied to trade value means no combat number can be tuned
 * without moving the economy. Damage is stored rather than remaining health,
 * so absence means whole and a piece that is minted, promoted or carried is
 * undamaged by construction rather than by somebody remembering to
 * initialise it.
 *
 * **Immunity is `canBeTaken`, and it is asked first.** Not a king, not an
 * armoured piece — being shot is being taken, and this is that rule's fifth
 * caller rather than a second copy of it. Asked *before* anything is derived
 * from value: `PIECE_VALUE[KING]` is zero on purpose (a king is never traded),
 * so any code that computed a king's maximum health from its value would give
 * it none and kill it on the first tick it was in range. `maxHealthOf` keeps
 * that nought for a *last* king deliberately and by a different route — every
 * blow against it is lethal, which is what keeps mate the only way to finish
 * an army — and `canBeHurt` is what stops one ever being struck at all.
 *
 * **Death is a capture in every respect that matters.** It goes through
 * `spillPiece`, so the corpse is the piece's *full* value, marked with the
 * side and rank that died, holdings spilled. A turret kill is a kill.
 *
 * Deliberately not the alternative — a corpse worth only *remaining* health,
 * so grinding a queen down destroys the material as it goes. That is tidy and
 * it is the wrong incentive: a turret stands over your own ground, so the
 * corpses fall where you can mine them, and burning them would mean defending
 * your own base impoverished you.
 *
 * **Armor is spent here and nowhere else**: no capture comes through this door,
 * so it stops everything that is not one. Overflow carries.
 *
 * Returns true when the piece died.
 */
export function damage(
  position: Position,
  industry: IndustryState,
  square: number,
  amount: number
): boolean {
  if (amount <= 0) return false;
  const piece = pieceAt(position, square);
  // Immunity first: a last king and an armoured piece take nothing.
  if (!piece || !canBeHurt(position, square, piece)) return false;

  // Deflector Shields (§31 A9) take theirs off before anything lands: a
  // blow they swallow whole hurts nothing, plate included.
  let landing = amount - wardAt(position, square);
  if (landing <= 0) return false;
  // Hurt this round, whatever the plate soaks: no regeneration for two (§31 K5).
  stampHurt(position, square);
  // The plate first; the rest carries on to the body. A local, not the parameter.
  const plate = platingAt(position, square);
  if (plate > 0) {
    setPlating(position, square, plate - landing);
    if (landing <= plate) return false;
    landing -= plate;
  }

  const taken = hurtAt(position, square) + landing;
  // Through `maxHealthOf`, so a variant's own row and the tough-piece override
  // are both what they are. It used to add `hurtAt` back onto `healthOf`, which
  // is the same number by a longer road.
  if (taken < maxHealthOf(position, square)) {
    setHurt(position, square, taken);
    return false;
  }

  // The industry is still what a corpse falls into — that is all it is for
  // here now that health lives on the position (§27 H1).
  spillPiece(industry, square, piece);
  setHurt(position, square, 0);
  removePiece(position, square);
  return true;
}

/**
 * Whose go it really is — a rules-level idea with one definition.
 *
 * Seats belong to the net layer and the core must not learn about them, so
 * this answers from what the core already knows: **in a run, the run's side is
 * one controller and every other side is the other**; outside a run, every
 * side is its own controller.
 *
 * That is what makes "control changed hands" mean the same thing in hot-seat,
 * in single player against one army, and in single player against four.
 */
export function controllerOf(state: GameState, side: Color): number {
  if (!state.run) return side;
  return side === state.run.side ? 0 : 1;
}

/**
 * Every Unicorn Knight of `mover`'s chips the enemy in its picket.
 *
 * **Once a round per Unicorn with nothing storing that**: inside the
 * handoff guard (where turrets fired before §31 S8), firing only the handing-over side's. So a Gambit pickets
 * nothing, Titan buys no damage, no search fires it. `docs/claude/armies.md`.
 */
export function runPickets(state: GameState, mover: Color): TurretShot[] {
  const { position, industry } = state;
  const shots: TurretShot[] = [];
  for (const at of unicornSquares(position, mover)) {
    const target = picketTarget(position, at, mover);
    if (target < 0) continue;
    const killed = damage(position, industry, target, RULES.picketDamage);
    // Every shot, hit or kill, so a laser can be drawn.
    shots.push({ from: at, to: target, killed });
  }
  return shots;
}
